import { DotsVerticalIcon, PlusCircleIcon } from "@heroicons/react/outline";
import { DropdownMenu } from "components/dropdownMenu";
import {
  INoPhaseTicketForm,
  IParentTicketForm,
  IParentTicketItem,
  IPhaseTicketForm,
  IPhaseTicketItem,
  ISimpleEventDetail,
  ITicketPhasesForm,
  ITicketsResponseData,
  Phase,
  ProductType,
  SupportedLanguage,
} from "interfaces";
import { get, isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { NoPhaseTicketForm } from "../forms/noPhaseTicketForm";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { CollapsibleSection } from "components/collapsibleSection";
import { SubTickets } from "./subTickets";
import { TicketForm } from "../forms/ticketForm";
import { useCreate, useOne, useTranslation, useUpdate } from "@refinedev/core";
import toast from "react-hot-toast";
import { ParentTicketForm } from "../forms/parentTicketForm";
import { HistoryLodModal } from "components/historyLogModal";
import { FullScreenLoading } from "components/fullScreenLoading";
import { endpointService } from "components/shared/services/endpointService";

interface IndividualTicketField extends IPhaseTicketItem {
  id: string;
}

interface ParentTicketModalConfig {
  open: boolean;
  defaultValues: IParentTicketItem | null;
  submit: (data: IParentTicketForm) => void;
}

const INITIAL_TICKET_MODAL_CONFIG = {
  open: false,
};

const INITIAL_PARENT_TICKET_MODAL_CONFIG: ParentTicketModalConfig = {
  open: false,
  defaultValues: null,
  submit: (data) => {},
};

const PrivateFlowTag = () => (
  <span className="max-w-full w-fit min-w-0 inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset bg-magnolia-500 text-brand-500 ring-magnolia-600">
    Private flow
  </span>
);

const ActiupPrivateFlowTag = () => (
  <span className="max-w-full w-fit min-w-0 inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset bg-brand-secondary-400 text-brand-secondary-500 ring-brand-secondary-300">
    ActiUp private
  </span>
);

const PrivateTags = ({ ticket }: { ticket?: IParentTicketItem | null }) => {
  if (!ticket?.is_use_private_flow && !ticket?.is_use_actiup_private_flow) return null;

  return (
    <div className="min-w-0 max-w-full flex flex-wrap items-center justify-end gap-2">
      {ticket?.is_use_private_flow ? <PrivateFlowTag /> : <></>}
      {ticket?.is_use_actiup_private_flow ? <ActiupPrivateFlowTag /> : <></>}
    </div>
  );
};

export const Tickets = ({
  eventDetailData,
  ticketPhases,
  fetchEventDetail,
}: {
  eventDetailData: ISimpleEventDetail;
  ticketPhases: Phase[];
  fetchEventDetail: () => void;
}) => {
  const { translate } = useTranslation();
  const [openLogModalId, setOpenLogModalId] = useState<string | null>(null);
  const [ticketModalConfig, setTicketModalConfig] = useState(
    INITIAL_TICKET_MODAL_CONFIG
  );
  const [parentTicketModalConfig, setParentTicketModalConfig] = useState(
    INITIAL_PARENT_TICKET_MODAL_CONFIG
  );
  const [openFieldId, setOpenFielIdId] = useState<string | null>(null);

  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [isDragged, setIsDragged] = useState(false);

  const { mutate, isLoading: isCreating } = useCreate();
  const { mutate: updateMutation, isLoading: isUpdating } = useUpdate();

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<ITicketPhasesForm>(); // retrieve all hook methods

  const { refetch: fetchTickets, data: ticketResponseData } =
    useOne<ITicketsResponseData>({
      resource: endpointService.getEventEndpoint("getTickets", {
        eventId: eventDetailData?.id ?? "",
      }),
      id: "",
      meta: {
        resource: "data",
      },
      queryOptions: {
        enabled: false,
      } as any,
    });

  const ticketsData = ticketResponseData?.data ?? null;

  const { fields, move, replace } = useFieldArray({
    control,
    name: "tickets",
  });

  useEffect(() => {
    if (isDragged && ticketsData) {
      replace(ticketsData.tickets);
      setIsDragged(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragged]);

  useEffect(() => {
    if (draggedItemId) {
      _moveField(
        fields.map((field) => field.ticket?.id ?? ""),
        draggedItemId
      );
      setDraggedItemId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggedItemId]);

  const _createPhaseTicket = (values: IPhaseTicketForm) => {
    const submitValues = {
      ticket: values.ticket,
      sub_tickets: [values.sub_ticket],
    };
    mutate(
      {
        resource: endpointService.getEventEndpoint("createParentTicket"),
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          event_id: eventDetailData.id,
          ...submitValues,
        },
      },
      {
        onSuccess: (data) => {
          fetchTickets();
          fetchEventDetail();
          toast.success(translate("toastMessage.addedSuccessfully"));
          _onCloseModal();
        },
      }
    );
  };

  const _createNoPhaseTicket = (submitValues: INoPhaseTicketForm) => {
    mutate(
      {
        resource: endpointService.getEventEndpoint("createSingleTicket"),
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          event_id: eventDetailData.id,
          tickets: [submitValues],
        },
      },
      {
        onSuccess: (data) => {
          fetchTickets();
          fetchEventDetail();
          toast.success(translate("toastMessage.addedSuccessfully"));
          _onCloseModal();
        },
      }
    );
  };

  const _updateParentTicket = (id: string, submitValues: IParentTicketForm) => {
    // const ticketId = get(watch("tickets")[ticketIndex].sub_tickets[subTicketIndex], "id", "");
    updateMutation(
      {
        resource: endpointService.getEventEndpoint("updateParentTicket", {
          id,
        }),
        id: "",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: (data) => {
          // const updatedSubTicket = get(data, "data.data", null) as ISubTicketItem;
          // if (updatedSubTicket) {
          //   setValue(`tickets.${ticketIndex}.sub_tickets.${subTicketIndex}`, updatedSubTicket);
          // }
          toast.success(translate("toastMessage.updatedSuccessfully"));
          _onCloseParentTicketModal();
          fetchTickets();
        },
      }
    );
  };

  const _onCloseModal = () => {
    setTicketModalConfig(INITIAL_TICKET_MODAL_CONFIG);
  };

  const _onCloseParentTicketModal = () => {
    setParentTicketModalConfig(INITIAL_PARENT_TICKET_MODAL_CONFIG);
  };

  const _handleOpenField = (id: string) => {
    setOpenFielIdId((prevId) => (prevId === id ? null : id));
  };

  const handleDrag = ({ source, destination }: any) => {
    if (destination && source.index !== destination.index) {
      move(source.index, destination.index);
      setDraggedItemId(fields[source.index].ticket?.id ?? null);
    }
  };

  const handleClickMove = (sourceIndex: number, destinationIndex: number) => {
    if (sourceIndex !== destinationIndex) {
      move(sourceIndex, destinationIndex);
      setDraggedItemId(fields[sourceIndex].ticket?.id ?? null);
    }
  };

  const _moveField = (ticketOrder: string[], movedTicketId: string) => {
    updateMutation(
      {
        resource: endpointService.getEventEndpoint("reorderTicket"),
        id: "",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          event_id: eventDetailData.id,
          ticket_order: ticketOrder,
          moved_ticket: movedTicketId,
        },
      },
      {
        onSuccess: (data) => {
          toast.success(translate("toastMessage.updatedSuccessfully"));
        },
        onSettled: async () => {
          await fetchTickets();
          setIsDragged(true);
        },
      }
    );
  };

  const ticketPhaseData = watch("ticket_phases");
  const ticketData = watch("tickets");

  return (
    <div className="box-container">
      <div>
        <DragDropContext onDragEnd={handleDrag}>
          <div className="grid sm:grid-cols-6 lg:gap-x-6">
            <Droppable droppableId="ticket-items" isDropDisabled={isUpdating}>
              {(provided, snapshot) => (
                <div
                  className="col-span-full"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {fields.map((field: IndividualTicketField, index: number) => (
                    <Draggable
                      key={`ticket[${index}]`}
                      draggableId={`item-${index}`}
                      isDragDisabled={isEmpty(field.ticket)}
                      index={index}
                    >
                      {(provided, snapshot) =>
                        !isEmpty(field.ticket) ? (
                          <div
                            className={`border rounded-lg mb-4 ${
                              get(errors, `tickets.${index}`, "")
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                            key={field.id}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <CollapsibleSection
                              label={`${get(
                                ticketData[index].ticket,
                                `category.${
                                  eventDetailData.languages.includes(
                                    SupportedLanguage.EN
                                  )
                                    ? "en"
                                    : eventDetailData.languages[0] ?? "vi"
                                }`
                              )}`}
                              customOpen={{
                                open: openFieldId === field.id,
                                onClick: () => _handleOpenField(field.id),
                              }}
                              additionalLabel={
                                <PrivateTags ticket={ticketData[index].ticket} />
                              }
                              isAnimated={false}
                              toolbar={
                                <DropdownMenu
                                  label={
                                    <DotsVerticalIcon className="w-4 h-4 text-brand-500" />
                                  }
                                  items={[
                                    {
                                      label: translate("boxAction.edit"),
                                      onClick: () =>
                                        setParentTicketModalConfig({
                                          open: true,
                                          submit: (data) =>
                                            _updateParentTicket(
                                              get(field, `ticket.id`, ""),
                                              data
                                            ),
                                          defaultValues: field.ticket,
                                        }),
                                    },
                                    ...(index !== 0
                                      ? [
                                          {
                                            label:
                                              translate("boxAction.moveUp"),
                                            onClick: () =>
                                              handleClickMove(index, index - 1),
                                          },
                                        ]
                                      : []),
                                    ...(index !== fields.length - 1
                                      ? [
                                          {
                                            label:
                                              translate("boxAction.moveDown"),
                                            onClick: () =>
                                              handleClickMove(index, index + 1),
                                          },
                                        ]
                                      : []),
                                    {
                                      label: "Log",
                                      onClick: () =>
                                        setOpenLogModalId(
                                          ticketData[index].ticket?.id ?? ""
                                        ),
                                    },
                                  ]}
                                />
                              }
                              {...provided.dragHandleProps}
                            >
                              <div className="px-4 py-4 sm:px-6">
                                <p className="mb-4">{translate("eventSetup.phaseAndTicket.sellInPhase")}</p>
                                <SubTickets
                                  eventDetailData={eventDetailData}
                                  ticketIndex={index}
                                  setOpenLogModalId={setOpenLogModalId}
                                />
                              </div>
                            </CollapsibleSection>
                          </div>
                        ) : (
                          <SubTickets
                            eventDetailData={eventDetailData}
                            ticketIndex={index}
                            key={field.id}
                            phaseTicket={false}
                            _moveField={_moveField}
                            setOpenLogModalId={setOpenLogModalId}
                          />
                        )
                      }
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>
        <div className="text-center text-sm">
          {isEmpty(fields) ? (
            <p className="text-center text-sm">
              {translate("eventSetup.phaseAndTicket.noTickets")}
            </p>
          ) : (
            <></>
          )}
        </div>
        <div className="w-full text-center">
          <button
            type="button"
            className="flex flex-row items-center mx-auto w-fit py-2.5 px-4 text-sm bg-white text-brand-500 active:bg-gray-50"
            onClick={() =>
              setTicketModalConfig({
                open: true,
              })
            }
          >
            <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
            <span className="shrink-0 text-sm font-medium">
              {translate("eventSetup.phaseAndTicket.addMoreTicket")}
            </span>
          </button>
        </div>
      </div>
      {isEmpty(ticketPhaseData) ? (
        <NoPhaseTicketForm
          productType={ProductType.TICKET}
          open={ticketModalConfig.open}
          onClose={_onCloseModal}
          eventDetailData={eventDetailData}
          defaultValues={null}
          onSubmit={(data) => _createNoPhaseTicket(data)}
        />
      ) : (
        <TicketForm
          productType={ProductType.TICKET}
          open={ticketModalConfig.open}
          onClose={_onCloseModal}
          eventDetailData={eventDetailData}
          ticketPhases={ticketPhases}
          onSubmit={(data) => _createPhaseTicket(data)}
        />
      )}
      <ParentTicketForm
        eventDetailData={eventDetailData}
        open={parentTicketModalConfig.open}
        onClose={_onCloseParentTicketModal}
        onSubmit={parentTicketModalConfig.submit}
        defaultValues={parentTicketModalConfig.defaultValues}
        title={`${translate("edit")} ${translate("eventSetup.phaseAndTicket.parentTicket")}`}
      />
      <HistoryLodModal
        resource={endpointService.getEventEndpoint("getTicketHistoryLog", {
          logId: openLogModalId ?? "",
        })}
        open={openLogModalId ? true : false}
        onClose={() => setOpenLogModalId(null)}
        key="ticket"
        id="ticket"
        customFilters={
          watch("tickets").find(
            (ticket) => ticket.ticket?.id === openLogModalId
          )
            ? [{ field: "parent_ticket", operator: "eq", value: true }]
            : []
        }
      />
      {isCreating || isUpdating ? <FullScreenLoading /> : <></>}
    </div>
  );
};
